import React, { useState, useEffect, useRef } from "react";
import emailjs from "@emailjs/browser";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin, Clock, Briefcase, CheckCircle2, Upload, Send, ChevronDown, Check } from "lucide-react";

const TECHNICAL_ROLES = [
  "Web Developer",
  "Application Developer",
  "Python Developer",
  "Data Analyst",
  "Machine Learning Engineer",
  "AI Engineer",
  "UI/UX Designer",
  "Other Technical Role",
];

const NON_TECHNICAL_ROLES = [
  "Business Development Associate (BDA)",
  "Digital Marketing Specialist",
  "Other Non-Technical Role",
];

const EMPLOYMENT_TYPES = [
  "Full-Time",
  "Part-Time",
  "Internship",
  "Contract / Freelance",
];

const EXPERIENCE_LEVELS = [
  "Fresher / 0 Years",
  "1 Year",
  "2 Years",
  "3+ Years",
];

const CustomSelect = ({ options, value, onChange, placeholder = "Select option" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="w-full px-4 py-2.5 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 font-medium text-sm flex items-center justify-between hover:bg-gray-100 hover:border-gray-400 focus:outline-none focus:border-gray-900 transition shadow-xs"
      >
        <span className="truncate">{value || placeholder}</span>
        <ChevronDown className={`w-4 h-4 text-gray-600 transition-transform duration-200 shrink-0 ml-2 ${isOpen ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 right-0 mt-1.5 bg-white border border-gray-200 rounded-xl shadow-xl py-1.5 z-40 max-h-56 overflow-y-auto"
          >
            {options?.map((option) => {
              const isSelected = option === value;
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => {
                    onChange(option);
                    setIsOpen(false);
                  }}
                  className={`w-full px-4 py-2.5 text-left text-sm font-medium flex items-center justify-between transition ${isSelected
                    ? "bg-gray-900 text-white font-semibold"
                    : "text-gray-800 hover:bg-gray-100"
                    }`}
                >
                  <span className="truncate">{option}</span>
                  {isSelected && <Check className="w-4 h-4 text-white shrink-0 ml-2" />}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const JobDetailsModal = ({ isOpen, onClose, selectedJob }) => {
  const formRef = useRef(null);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    employmentType: "Full-Time",
    roleCategory: "technical",
    role: TECHNICAL_ROLES[0],
    customRole: "",
    location: "",
    experience: "Fresher / 0 Years",
    skills: "",
    linkedin: "",
    github: "",
    portfolio: "",
    resume: null,
    resumeFileName: "",
    resumeUrl: "",
    whyJoin: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (selectedJob) {
      const isTech = TECHNICAL_ROLES.includes(selectedJob.title);
      setFormData((prev) => ({
        ...prev,
        roleCategory: isTech ? "technical" : "non-technical",
        role: selectedJob.title || TECHNICAL_ROLES[0],
        employmentType: selectedJob.type || "Full-Time",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        roleCategory: "technical",
        role: TECHNICAL_ROLES[0],
        employmentType: "Full-Time",
      }));
    }
    setSubmitted(false);
    setIsSubmitting(false);
    setErrorMessage("");
  }, [selectedJob, isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 500 * 1024) {
        setErrorMessage(
          `File "${file.name}" is ${(file.size / 1024).toFixed(0)}KB. EmailJS attachments must be under 500KB. Please select a smaller file or paste a Google Drive link below.`
        );
      } else {
        setErrorMessage("");
      }
      setFormData((prev) => ({
        ...prev,
        resume: file,
        resumeFileName: file.name,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formRef.current) return;

    setIsSubmitting(true);
    setErrorMessage("");

    let fileDownloadUrl = formData.resumeUrl || "";

    // Upload candidate's file to generate a direct download link without hitting EmailJS 50KB limit
    if (formData.resume) {
      try {
        const fileData = new FormData();
        fileData.append("file", formData.resume);

        const uploadRes = await fetch("https://tmpfiles.org/api/v1/upload", {
          method: "POST",
          body: fileData,
        });

        if (uploadRes.ok) {
          const uploadJson = await uploadRes.json();
          if (uploadJson?.data?.url) {
            fileDownloadUrl = uploadJson.data.url.replace("tmpfiles.org/", "tmpfiles.org/dl/");
          }
        }
      } catch (uploadErr) {
        console.warn("File upload to cloud storage failed:", uploadErr);
      }
    }

    // Set hidden inputs dynamically before sendForm
    const linkInput = formRef.current.querySelector('input[name="resume_download_link"]');
    if (linkInput) {
      linkInput.value = fileDownloadUrl || formData.resumeUrl || "N/A";
    }

    const msgInput = formRef.current.querySelector('input[name="message"]');
    if (msgInput) {
      const chosenRole = formData.customRole || formData.role;
      msgInput.value = `NEW JOB APPLICATION RECEIVED

Candidate Details:
- Name: ${formData.fullName}
- Email: ${formData.email}
- Phone: ${formData.phone}
- Applied Position: ${chosenRole} (${formData.employmentType})
- Location: ${formData.location || "N/A"}

Professional Details:
- Experience: ${formData.experience}
- Skills: ${formData.skills || "N/A"}
- LinkedIn: ${formData.linkedin || "N/A"}
- GitHub: ${formData.github || "N/A"}
- Portfolio: ${formData.portfolio || "N/A"}

Resume & Uploaded File:
- File Name: ${formData.resumeFileName || "Not uploaded"}
- Direct Download Link: ${fileDownloadUrl || formData.resumeUrl || "N/A"}

Why Join Motivation:
${formData.whyJoin || "N/A"}

Submission Details:
Date: ${new Date().toLocaleDateString("en-IN")}
Time: ${new Date().toLocaleTimeString("en-IN")}`;
    }

    // Temporarily detach name attribute from file input so emailjs.sendForm doesn't try to send binary data in variables payload
    const fileInput = formRef.current.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.removeAttribute("name");
    }

    const serviceId = "service_4b3m67w";
    const templateId = "template_cwtnl7t";
    const publicKey = "4AK_G8ie9sKNAFjA5";

    try {
      await emailjs.sendForm(
        serviceId,
        templateId,
        formRef.current,
        publicKey
      );

      if (fileInput) {
        fileInput.setAttribute("name", "resume");
      }

      setIsSubmitting(false);
      setSubmitted(true);
    } catch (error) {
      if (fileInput) {
        fileInput.setAttribute("name", "resume");
      }
      console.error("EmailJS submission failed:", error);

      // Fallback: If EmailJS fails due to Gmail OAuth expiration ("Gmail_API: Invalid grant"), send via fallback endpoint to guarantee email delivery
      try {
        const chosenRole = formData.customRole || formData.role;
        const formDataToSend = new FormData();
        formDataToSend.append("_subject", `New Job Application: ${chosenRole} (${formData.employmentType}) - ${formData.fullName}`);
        formDataToSend.append("_replyto", formData.email);
        formDataToSend.append("_captcha", "false");
        formDataToSend.append("Candidate Name", formData.fullName);
        formDataToSend.append("Email Address", formData.email);
        formDataToSend.append("Phone Number", formData.phone);
        formDataToSend.append("Applied Position", `${chosenRole} (${formData.employmentType})`);
        formDataToSend.append("Location", formData.location || "N/A");
        formDataToSend.append("Experience", formData.experience);
        formDataToSend.append("Primary Skills", formData.skills || "N/A");
        formDataToSend.append("LinkedIn Profile", formData.linkedin || "N/A");
        formDataToSend.append("GitHub Profile", formData.github || "N/A");
        formDataToSend.append("Portfolio Website", formData.portfolio || "N/A");
        formDataToSend.append("Uploaded Resume Name", formData.resumeFileName || "Not uploaded");
        formDataToSend.append("Resume Cloud Download Link", fileDownloadUrl || formData.resumeUrl || "N/A");
        formDataToSend.append("Why Join Motivation", formData.whyJoin || "N/A");

        if (formData.resume) {
          formDataToSend.append("attachment", formData.resume, formData.resumeFileName);
        }

        const fallbackRes = await fetch("https://formsubmit.co/ajax/crackdeveloperschennai@gmail.com", {
          method: "POST",
          headers: { Accept: "application/json" },
          body: formDataToSend,
        });

        if (fallbackRes.ok) {
          setIsSubmitting(false);
          setSubmitted(true);
          return;
        }
      } catch (fallbackErr) {
        console.error("Fallback submission failed:", fallbackErr);
      }

      setIsSubmitting(false);
      const isGmailGrantError = error?.text?.includes("Gmail_API: Invalid grant") || error?.message?.includes("Invalid grant");
      const errorMsg = isGmailGrantError
        ? "Gmail_API: Invalid grant. Please reconnect your Gmail account in the EmailJS dashboard (Email Services -> Gmail Service -> Reconnect Account)."
        : error?.text || error?.message || "Failed to send application. Please try again or check your connection.";
      setErrorMessage(errorMsg);
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm"
        />

        {/* Modal Container */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-3xl max-h-[90vh] bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden z-10 flex flex-col my-auto text-gray-900 font-sans"
        >
          {/* Top Header */}
          <div className="p-6 md:p-8 border-b border-gray-200 flex items-start justify-between bg-white sticky top-0 z-20">
            <div>
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200 inline-block mb-2">
                {selectedJob ? selectedJob.category : "Career Application"}
              </span>
              <h2 className="text-2xl md:text-3xl font-semibold text-gray-900">
                {selectedJob ? `Apply for ${selectedJob.title}` : "Apply to Crack Developers"}
              </h2>

              {selectedJob && (
                <div className="flex flex-wrap gap-4 text-xs text-gray-600 font-medium mt-2">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5 text-gray-500" />
                    {selectedJob.location}
                  </span>
                  <span className="flex items-center gap-1">
                    <Briefcase className="w-3.5 h-3.5 text-gray-500" />
                    {selectedJob.experience}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5 text-gray-500" />
                    {selectedJob.type}
                  </span>
                </div>
              )}
            </div>

            <button
              onClick={onClose}
              className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:text-gray-900 hover:bg-gray-200 transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Modal Body */}
          <div className="p-6 md:p-8 overflow-y-auto space-y-6 flex-1">
            {submitted ? (
              <div className="text-center py-12 space-y-4">
                <div className="w-16 h-16 rounded-full bg-gray-900 text-white flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-10 h-10" />
                </div>
                <h3 className="text-2xl font-semibold text-gray-900">Application Received!</h3>
                <p className="text-gray-600 text-base max-w-md mx-auto leading-relaxed">
                  Thank you for applying for <span className="text-gray-900 font-semibold">{formData.customRole || formData.role}</span> ({formData.employmentType}). Our team will review your profile and reach out to you shortly.
                </p>
                <button
                  onClick={onClose}
                  className="mt-6 px-6 py-2.5 rounded-lg bg-gray-900 text-white font-medium text-sm hover:bg-gray-800 transition"
                >
                  Close Window
                </button>
              </div>
            ) : (
              /* Application Form */
              <form ref={formRef} onSubmit={handleSubmit} className="space-y-5">
                {/* Hidden fields for EmailJS template parameters */}
                <input type="hidden" name="to_email" value="crackdeveloperschennai@gmail.com" />
                <input type="hidden" name="to_name" value="Crack Developers Hiring Team" />
                <input type="hidden" name="name" value={formData.fullName} />
                <input type="hidden" name="from_name" value={formData.fullName} />
                <input type="hidden" name="email" value={formData.email} />
                <input type="hidden" name="from_email" value={formData.email} />
                <input type="hidden" name="reply_to" value={formData.email} />
                <input type="hidden" name="phone" value={formData.phone} />
                <input type="hidden" name="position" value={`${formData.customRole || formData.role} (${formData.employmentType})`} />
                <input type="hidden" name="applied_role" value={formData.customRole || formData.role} />
                <input type="hidden" name="employment_type" value={formData.employmentType} />
                <input type="hidden" name="experience" value={formData.experience} />
                <input type="hidden" name="location" value={formData.location || "N/A"} />
                <input type="hidden" name="skills" value={formData.skills || "N/A"} />
                <input type="hidden" name="linkedin" value={formData.linkedin || "N/A"} />
                <input type="hidden" name="github" value={formData.github || "N/A"} />
                <input type="hidden" name="portfolio" value={formData.portfolio || "N/A"} />
                <input type="hidden" name="resume_url" value={formData.resumeUrl || "N/A"} />
                <input type="hidden" name="resume_download_link" value="" />
                <input type="hidden" name="resume_file_name" value={formData.resumeFileName || "Not uploaded"} />
                <input type="hidden" name="why_join" value={formData.whyJoin || "N/A"} />
                <input type="hidden" name="message" value={formData.whyJoin || "N/A"} />
                <input type="hidden" name="date" value={new Date().toLocaleDateString("en-IN")} />
                <input type="hidden" name="time" value={new Date().toLocaleTimeString("en-IN")} />
                <input type="hidden" name="subject" value={`New Job Application: ${formData.customRole || formData.role} (${formData.employmentType}) - ${formData.fullName}`} />

                {errorMessage && (
                  <div className="p-3.5 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs font-medium leading-relaxed">
                    {errorMessage}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Full Name */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="fullName"
                      required
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder="John Doe"
                      className="w-full px-4 py-2.5 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 placeholder-gray-400 focus:border-gray-900 focus:outline-none text-sm"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      className="w-full px-4 py-2.5 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 placeholder-gray-400 focus:border-gray-900 focus:outline-none text-sm"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+91 98765 43210"
                      className="w-full px-4 py-2.5 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 placeholder-gray-400 focus:border-gray-900 focus:outline-none text-sm"
                    />
                  </div>

                  {/* Employment Type */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Employment Type *
                    </label>
                    <CustomSelect
                      options={EMPLOYMENT_TYPES}
                      value={formData.employmentType}
                      onChange={(val) => setFormData((prev) => ({ ...prev, employmentType: val }))}
                      placeholder="Select Employment Type"
                    />
                  </div>
                </div>

                {/* Role Domain & Specific Role Selection */}
                <div className="space-y-4 pt-1 border-t border-gray-100">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
                    {/* Role Domain (Technical vs Non-Technical) */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        Role Category *
                      </label>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setFormData((prev) => ({
                              ...prev,
                              roleCategory: "technical",
                              role: TECHNICAL_ROLES[0],
                            }));
                          }}
                          className={`flex-1 py-2.5 px-3 rounded-lg text-xs font-semibold border transition ${formData.roleCategory === "technical"
                            ? "bg-gray-900 text-white border-gray-900 shadow-xs"
                            : "bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100"
                            }`}
                        >
                          Technical Roles
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setFormData((prev) => ({
                              ...prev,
                              roleCategory: "non-technical",
                              role: NON_TECHNICAL_ROLES[0],
                            }));
                          }}
                          className={`flex-1 py-2.5 px-3 rounded-lg text-xs font-semibold border transition ${formData.roleCategory === "non-technical"
                            ? "bg-gray-900 text-white border-gray-900 shadow-xs"
                            : "bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100"
                            }`}
                        >
                          Non-Technical Roles
                        </button>
                      </div>
                    </div>

                    {/* Specific Role Dropdown */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        Select Role *
                      </label>
                      <CustomSelect
                        options={formData.roleCategory === "technical" ? TECHNICAL_ROLES : NON_TECHNICAL_ROLES}
                        value={formData.role}
                        onChange={(val) => setFormData((prev) => ({ ...prev, role: val }))}
                        placeholder="Select Role"
                      />
                    </div>
                  </div>

                  {/* Custom Role input if "Other" selected */}
                  {(formData.role === "Other Technical Role" || formData.role === "Other Non-Technical Role") && (
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        Specify Your Desired Role *
                      </label>
                      <input
                        type="text"
                        name="customRole"
                        required
                        value={formData.customRole}
                        onChange={handleChange}
                        placeholder="e.g. Data Scientist, Account Executive"
                        className="w-full px-4 py-2.5 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 placeholder-gray-400 focus:border-gray-900 focus:outline-none text-sm"
                      />
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                  {/* Current Location */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Current Location
                    </label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      placeholder="Chennai, India"
                      className="w-full px-4 py-2.5 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 placeholder-gray-400 focus:border-gray-900 focus:outline-none text-sm"
                    />
                  </div>

                  {/* Experience */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Years of Experience
                    </label>
                    <CustomSelect
                      options={EXPERIENCE_LEVELS}
                      value={formData.experience}
                      onChange={(val) => setFormData((prev) => ({ ...prev, experience: val }))}
                      placeholder="Select Experience Level"
                    />
                  </div>
                </div>

                {/* Skills */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Primary Skills / Technologies
                  </label>
                  <input
                    type="text"
                    name="skills"
                    value={formData.skills}
                    onChange={handleChange}
                    placeholder="e.g. React, Node.js, Python, Figma"
                    className="w-full px-4 py-2.5 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 placeholder-gray-400 focus:border-gray-900 focus:outline-none text-sm"
                  />
                </div>

                {/* Links row */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      LinkedIn URL
                    </label>
                    <input
                      type="url"
                      name="linkedin"
                      value={formData.linkedin}
                      onChange={handleChange}
                      placeholder="https://linkedin.com/in/..."
                      className="w-full px-4 py-2.5 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 placeholder-gray-400 focus:border-gray-900 focus:outline-none text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      GitHub URL
                    </label>
                    <input
                      type="url"
                      name="github"
                      value={formData.github}
                      onChange={handleChange}
                      placeholder="https://github.com/..."
                      className="w-full px-4 py-2.5 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 placeholder-gray-400 focus:border-gray-900 focus:outline-none text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Portfolio / Website
                    </label>
                    <input
                      type="url"
                      name="portfolio"
                      value={formData.portfolio}
                      onChange={handleChange}
                      placeholder="https://yourportfolio.com"
                      className="w-full px-4 py-2.5 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 placeholder-gray-400 focus:border-gray-900 focus:outline-none text-sm"
                    />
                  </div>
                </div>

                {/* Resume Upload / Link */}
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Resume Upload / File *
                    </label>
                    <label className="flex flex-col items-center justify-center p-4 rounded-lg border border-dashed border-gray-300 bg-gray-50 hover:border-gray-900 cursor-pointer transition">
                      <Upload className="w-5 h-5 text-gray-900 mb-1" />
                      <span className="text-xs text-gray-600 font-medium">
                        {formData.resumeFileName ? (
                          <span className="text-gray-900 font-semibold">{formData.resumeFileName}</span>
                        ) : (
                          "Click to select PDF or DOCX resume file"
                        )}
                      </span>
                      <input
                        type="file"
                        name="resume"
                        id="resume"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                    </label>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Or Direct Resume Download Link (Google Drive / Dropbox / Cloud)
                    </label>
                    <input
                      type="url"
                      name="resumeUrl"
                      value={formData.resumeUrl}
                      onChange={handleChange}
                      placeholder="https://drive.google.com/file/d/..."
                      className="w-full px-4 py-2.5 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 placeholder-gray-400 focus:border-gray-900 focus:outline-none text-sm"
                    />
                  </div>
                </div>

                {/* Why Join */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Why do you want to join Crack Developers?
                  </label>
                  <textarea
                    name="whyJoin"
                    rows="3"
                    value={formData.whyJoin}
                    onChange={handleChange}
                    placeholder="Tell us briefly what drives you and what you're excited to build..."
                    className="w-full px-4 py-2.5 rounded-lg bg-gray-50 border border-gray-300 text-gray-900 placeholder-gray-400 focus:border-gray-900 focus:outline-none text-sm"
                  ></textarea>
                </div>

                {/* Submit button */}
                <div className="pt-4 flex justify-end">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full sm:w-auto px-6 py-3 rounded-lg bg-gray-900 text-white font-medium text-sm hover:bg-gray-800 transition flex items-center justify-center gap-2 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Sending Application...</span>
                      </>
                    ) : (
                      <>
                        <span>Submit Application</span>
                        <Send className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default JobDetailsModal;
